/* eslint-disable no-magic-numbers */
import { ConfigData, RequestData } from '../config/StoreLocatorTextResources';
import { STORELOCATORCOMPONENT } from '../components/storelocator.component';
import { MULTIPLELOCATIONSSERVICE } from './multiple.locations.service';
import { NOMINATIM } from './nominatim.service';
import { HELPER } from './helper.service';

export class RouteDirectionsService {
  /**
   * Propertys
   */
  directionsRenderer: google.maps.DirectionsRenderer;
  directionsService: google.maps.DirectionsService;
  currenLocationCoords: object = null;
  travelMode: string = 'DRIVING';
  geoCoords: object = null;
  map: google.maps.Map;
  latlng: object;

  createRouteCalculation() {
    // Instantiate a directions service.
    this.directionsService = new google.maps.DirectionsService();
    // Create a renderer for directions and bind it to the map.
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: { strokeColor: '#AA4A44' },
    });
    this.geoCoords = STORELOCATORCOMPONENT.geoCoords;

    MULTIPLELOCATIONSSERVICE.search.getElementsByClassName('pac-target-input')[0].addEventListener('change', () => {
      this.resetDirectionsMap();
      this.centerMapOnCurrentLocation();
      this.clearTravelMode();
    });

    const clearButton = MULTIPLELOCATIONSSERVICE.search.getElementsByClassName('pac-target-button icon-close')[0];
    clearButton.addEventListener('click', () => {
      this.resetDirectionsMap();
      this.centerMapOnCurrentLocation();
    });
  }

  resetDirectionsMap() {
    this.directionsRenderer.set('directions', null);
  }

  setTravelMode(mode: string) {
    this.travelMode = mode;
  }

  clearTravelMode() {
    const btns = document.querySelectorAll('input[type="radio"]');
    for (let i = 0; i < btns.length; i++) {
      btns[i]['checked'] = false;
    }
  }

  centerMapOnCurrentLocation() {
    let coords;
    if (ConfigData.data['singleLocations']) {
      coords = this.currenLocationCoords;
    } else {
      coords = this.geoCoords;
    }
    this.map.setCenter(coords);
    if (ConfigData.data['singleLocations']) {
      this.map.setZoom(17);
    }
  }

  getDestination(latlng) {
    return new Promise((resolve, reject) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[1]) {
          resolve(results[1].place_id);
        } else {
          reject(status);
        }
      });
    });
  }

  /**
   * https://developers.google.com/maps/documentation/javascript/examples/directions-simple
   * https://developers.google.com/maps/documentation/javascript/examples/directions-panel
   * @param panel
   */
  calculateAndDisplayRoute() {
    this.directionsService.route(
      {
        origin: this.getOrigin(this.map),
        destination: this.latlng,
        travelMode: google.maps.TravelMode[this.travelMode],
      },
      (response, status) => {
        if (status == 'OK') {
          this.directionsRenderer.setDirections(response);
        } else {
          window.alert('Directions request failed due to ' + status);
        }
      },
    );
  }

  getOrigin(map) {
    let origin;

    if (this.geoCoords !== null) {
      origin = this.geoCoords;
    } else {
      const updateOrigin = () => {
        const center = map.getCenter();
        const lat = center.lat();
        const lng = center.lng();
        return { lat, lng };
      };

      origin = updateOrigin();

      // Listen for the center_changed event of the map
      map.addListener('center_changed', () => {
        // Check if the zoom level is greater than 10
        if (map.getZoom() > 10) {
          origin = updateOrigin();
        }
      });
    }
    return origin;
  }

  setCityCoordinates(latitude, longitude) {
    this.geoCoords = { lat: Number(latitude), lng: Number(longitude) };
  }

  getDistance(destinationLatitude, destinationLongitude, cityValue?) {
    if (!destinationLatitude || !destinationLongitude) {
      return 0;
    }

    const currentCityCoordinats = cityValue ? this.getCurrentCityCoords(cityValue) : this.geoCoords;
    const destCoordinats = currentCityCoordinats;
    const distance = this.distanceBetweenPoints(destCoordinats, {
      lat: destinationLatitude,
      lng: destinationLongitude,
    });

    return distance;
  }

  distanceBetweenPoints(p1, p2) {
    if (!p1 || !p2) {
      return 0;
    }

    const R = 6371; // Radius of the Earth in km
    const dLat = ((p2.lat - p1.lat) * Math.PI) / 180;
    const dLon = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((p1.lat * Math.PI) / 180) * Math.cos((p2.lat * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return (R * c).toFixed(2);
  }

  getCurrentCity(cityValue) {
    let currentCity;

    if (RequestData.request['location'].city !== undefined) {
      currentCity = RequestData.request['location'].city;
    } else if (cityValue) {
      currentCity = cityValue;
    }
    return currentCity;
  }

  getCurrentCityCoords(cityValue: string) {
    let currentCityCoords: object;

    const currentCityValue = this.getCurrentCity(cityValue);
    this.nominatimRequest(currentCityValue, (request: object) => {
      this.geoCoords = request;
      currentCityCoords = request;
    });

    return currentCityCoords;
  }

  nominatimRequest(searchValue, request) {
    if (isNaN(searchValue)) {
      const countryCode = ConfigData.data['countrycode'].substring(3);
      const requestParameter = `${'?q=' + searchValue + '&countrycodes=' + countryCode.toLowerCase() + '&limit=1'}`;

      NOMINATIM.executeRequest(requestParameter, (callback) => {
        if (callback.length > 0) {
          this.setCityCoordinates(callback[0].lat, callback[0].lon);
          request({ lat: Number(callback[0].lat), lng: Number(callback[0].lon) });
        }
      });
    }
  }

  initDirectionsService(element) {
    const panel = element.querySelector('#directionsPanel' + element.id);
    const renderOptions = {
      suppressMarkers: true,
      suppressInfoWindows: true,
      polylineOptions: {
        strokeColor: 'rgb(160 13 12)',
      },
    };

    // Instantiate a directions service.
    this.directionsService = new google.maps.DirectionsService();
    // Create a renderer for directions and bind it to the map.
    this.directionsRenderer = new google.maps.DirectionsRenderer(renderOptions);

    this.directionsRenderer.setMap(this.map);
    this.directionsRenderer.setPanel(panel as HTMLElement);
  }

  handleButtonItinerary(element) {
    const itineraryButton = element.querySelector('cwc-location-details .button-itinerary');

    itineraryButton.addEventListener('click', () => {
      ConfigData.data['calculateRoute'] = true;
      this.geoCoords = STORELOCATORCOMPONENT.geoCoords;
      this.handleRouteCalculation(element);
    });
  }

  handleRouteCalculation(element) {
    const deviceInfo = ConfigData.data['deviceInfo'].os();
    const destinationInput = element.querySelector('#destination-input' + element.id);

    const [latitude, longitude] = destinationInput.value.split(',');
    const latlng = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
    this.latlng = latlng;

    if (deviceInfo !== null) {
      this.startMobileNavigation(deviceInfo);
    } else {
      const locationDetails = element.querySelector('cwc-location-details');
      const locationItinerary = element.querySelector('cwc-location-itinerary');

      if (locationDetails) {
        HELPER.changeClass(locationDetails, 'show', 'hide');
      }
      if (locationItinerary) {
        HELPER.changeClass(locationItinerary, 'hide', 'show');
      }
      this.startRouteCalculation(element);
    }
  }

  startRouteCalculation(element) {
    const modeSelectors = element.querySelectorAll('#mode-selector input');

    this.initDirectionsService(element);
    modeSelectors[0].checked = true;

    for (const selector of modeSelectors) {
      if (selector.checked) {
        this.calculateAndDisplayRoute();
      }

      selector.addEventListener('click', () => {
        let selectedMode = selector.id;
        selectedMode = selectedMode.replace(new RegExp(/[0-9]+/), '');
        selectedMode = selectedMode.toUpperCase();

        this.setTravelMode(selectedMode);
        this.calculateAndDisplayRoute();
      });
    }
  }

  startMobileNavigation(deviceInfo) {
    const destination = `${this.latlng['lat']},${this.latlng['lng']}`;
    const iosVersion = ConfigData.data['deviceInfo'].version('iOS');
    let domain: string = 'google.com';

    if (deviceInfo === 'iOS' && iosVersion > 5) {
      domain = 'apple.com';
    }

    // const navURL = "https://maps.google.com/maps?saddr="+`${this.geoCoords['lat']},${this.geoCoords['lng']}`+"&daddr="+`${this.latlng['lat']},${this.latlng['lng']}`;
    const navURL = 'https://maps.' + domain + '/maps?saddr=Current+Location&daddr=' + destination;

    window.location.href = navURL;
  }
}

export const ROUTE = new RouteDirectionsService();
